import { useState, useEffect, useRef } from "react";
import { storyElement } from "@/lib/story-element";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, MessageCircle, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { useLocale } from "@/hooks/useLocale";
import { fmtTime } from "@/lib/locale-format";

interface ChatMessage {
  id: string;
  adventure_id: string;
  user_id: string;
  sender_name: string;
  content: string;
  created_at: string;
}

interface PlayerChatProps {
  adventureId: string;
  currentUserId: string | null;
  senderName: string;
  embedded?: boolean;
  onUnreadChange?: (count: number) => void;
}

export function PlayerChat({ adventureId, currentUserId, senderName, embedded, onUnreadChange }: PlayerChatProps) {
  const { t, locale } = useLocale();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const channelRef = useRef<any>(null);
  // Stable ref so the subscription always calls the latest onUnreadChange prop
  // without needing to re-subscribe every time the parent re-renders.
  const onUnreadChangeRef = useRef(onUnreadChange);
  useEffect(() => { onUnreadChangeRef.current = onUnreadChange; });

  useEffect(() => {
    supabase
      .from("player_messages")
      .select("*")
      .eq("adventure_id", adventureId)
      .order("created_at", { ascending: true })
      .limit(200)
      .then(({ data }) => {
        if (data) setMessages(data as any[]);
      });

    const channel = supabase
      .channel(`player-chat-${adventureId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "player_messages",
          filter: `adventure_id=eq.${adventureId}`,
        },
        (payload) => {
          const msg = payload.new as ChatMessage;
          setMessages((prev) => {
            if (prev.some((m) => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
          if (msg.user_id !== currentUserId) {
            setUnread((prev) => {
              const next = prev + 1;
              onUnreadChangeRef.current?.(next);
              return next;
            });
          }
        }
      )
      .subscribe();

    channelRef.current = channel;
    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [adventureId, currentUserId]);

  useEffect(() => {
    requestAnimationFrame(() => {
      const el = scrollContainerRef.current;
      if (el) {
        const viewport = el.querySelector("[data-radix-scroll-area-viewport]");
        if (viewport) {
          viewport.scrollTop = viewport.scrollHeight;
        }
      }
    });
  }, [messages]);

  useEffect(() => {
    // Use stable ref so the latest callback is called even if parent re-renders
    if (isOpen || embedded) { setUnread(0); onUnreadChangeRef.current?.(0); }
  }, [isOpen, embedded]);

  const handleSend = async () => {
    if (!input.trim() || !currentUserId) return;
    const content = input.trim();
    setInput("");

    const { error } = await supabase.from("player_messages").insert({
      adventure_id: adventureId,
      user_id: currentUserId,
      sender_name: senderName || t.play_anonymous,
      content,
    } as any);

    if (error) toast.error(t.play_send_failed);
  };

  // Embedded mode: no toggle, just content
  if (embedded) {
    return (
      <div {...storyElement("player-chat")} className="flex flex-col h-full">
        <ScrollArea className="flex-1 px-3 py-2" ref={scrollContainerRef}>
          <div className="space-y-1.5">
            {messages.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">
                {t.play_no_messages}
              </p>
            )}
            {messages.map((msg) => {
              const isMe = msg.user_id === currentUserId;
              return (
                <div key={msg.id} className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs ${
                      isMe
                        ? "bg-primary/15 border border-primary/20 text-foreground"
                        : "bg-muted/50 border border-border text-foreground"
                    }`}
                  >
                    {!isMe && (
                      <span className="font-semibold text-primary block text-[10px]">
                        {msg.sender_name}
                      </span>
                    )}
                    <span>{msg.content}</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground mt-0.5 px-1">
                    {fmtTime(msg.created_at, locale, { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              );
            })}
          </div>
        </ScrollArea>
        <div className="flex gap-1.5 p-2 border-t border-border shrink-0">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={t.play_message_party}
            className="flex-1 h-7 text-xs bg-background"
          />
          <Button onClick={handleSend} disabled={!input.trim()} size="icon" aria-label={t.aria_send_message} className="h-7 w-7">
            <Send className="w-3 h-3" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div {...storyElement("player-chat")} className="border-t border-border bg-card/50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-4 py-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        <MessageCircle className="w-3.5 h-3.5" />
        <span>{t.play_party_chat}</span>
        {unread > 0 && (
          <span className="bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 text-xs font-bold min-w-[18px] text-center">
            {unread}
          </span>
        )}
        <span className="ml-auto">
          {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
        </span>
      </button>

      {isOpen && (
        <div className="flex flex-col" style={{ height: "240px" }}>
          <ScrollArea className="flex-1 px-3 py-2" ref={scrollContainerRef}>
            <div className="space-y-1.5">
              {messages.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">
                  {t.play_no_messages}
                </p>
              )}
              {messages.map((msg) => {
                const isMe = msg.user_id === currentUserId;
                return (
                  <div key={msg.id} className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}>
                    <div
                      className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs ${
                        isMe
                          ? "bg-primary/15 border border-primary/20 text-foreground"
                          : "bg-muted/50 border border-border text-foreground"
                      }`}
                    >
                      {!isMe && (
                        <span className="font-semibold text-primary block text-[10px]">
                          {msg.sender_name}
                        </span>
                      )}
                      <span>{msg.content}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground mt-0.5 px-1">
                      {fmtTime(msg.created_at, locale, { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          <div className="flex gap-1.5 p-2 border-t border-border">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={t.play_message_party}
              className="flex-1 h-7 text-xs bg-background"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim()}
              size="icon"
              aria-label={t.aria_send_message}
              className="h-7 w-7"
            >
              <Send className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
